"use client";

import { MapPin } from "lucide-react";
import React, { useEffect, useRef } from "react";

interface MapPointPickerLayerProps {
  /** Wrapper around the embedded MapView; the marker is positioned relative to it. */
  mapWrapperRef: React.RefObject<HTMLDivElement | null>;
  /** Currently picked point in SVG user space, or null. */
  point: { x: number; y: number } | null;
  onChange: (point: { x: number; y: number }) => void;
}

const CLICK_MOVE_THRESHOLD = 6; // px — distinguishes a click from a pan/drag.

/** The map's floor `<svg>` is the first svg inside the `.map-container` element. */
const getMapSvg = (wrapper: HTMLDivElement | null): SVGSVGElement | null =>
  (wrapper?.querySelector(".map-container svg") as SVGSVGElement | null) ??
  null;

/**
 * Lets the user drop a single pin on the embedded map and reports its position in SVG
 * user-space coordinates (which survive pan & zoom). Renders the marker inside the map
 * wrapper (not a body portal) so it sits above the map within the modal's stacking context.
 */
export const MapPointPickerLayer: React.FC<MapPointPickerLayerProps> = ({
  mapWrapperRef,
  point,
  onChange
}) => {
  const markerRef = useRef<HTMLDivElement | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Capture a click on the map background and convert it to SVG user space.
  useEffect(() => {
    let downX = 0;
    let downY = 0;
    const onDown = (e: MouseEvent) => {
      downX = e.clientX;
      downY = e.clientY;
    };
    const onUp = (e: MouseEvent) => {
      if (
        Math.hypot(e.clientX - downX, e.clientY - downY) > CLICK_MOVE_THRESHOLD
      ) {
        return; // a pan/drag, not a click
      }
      const target = e.target as Element | null;
      if (!target?.closest(".map-container")) return; // outside the map
      if (target.closest("[data-map-point]")) return; // our own marker

      const svg = getMapSvg(mapWrapperRef.current);
      const ctm = svg?.getScreenCTM();
      if (!svg || !ctm) return;
      const p = svg.createSVGPoint();
      p.x = e.clientX;
      p.y = e.clientY;
      const mapPoint = p.matrixTransform(ctm.inverse());
      onChangeRef.current({ x: mapPoint.x, y: mapPoint.y });
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("mouseup", onUp);
    };
  }, [mapWrapperRef]);

  // Keep the marker glued to its map coordinate (relative to the wrapper) on pan/zoom.
  useEffect(() => {
    if (!point) return;
    const wrapper = mapWrapperRef.current;
    const svg = getMapSvg(wrapper);
    if (!wrapper || !svg) return;
    let raf = 0;
    const tick = () => {
      const ctm = svg.getScreenCTM();
      const el = markerRef.current;
      if (ctm && el) {
        const p = svg.createSVGPoint();
        p.x = point.x;
        p.y = point.y;
        const screen = p.matrixTransform(ctm);
        const rect = wrapper.getBoundingClientRect();
        el.style.transform = `translate(${screen.x - rect.left}px, ${
          screen.y - rect.top
        }px)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [point, mapWrapperRef]);

  if (!point) {
    return null;
  }

  return (
    <div
      ref={markerRef}
      data-map-point
      className="pointer-events-none absolute top-0 left-0 z-[50]"
    >
      {/* Pin marker — tip sits exactly on the clicked map point. */}
      <div className="-translate-x-1/2 -translate-y-full absolute">
        <span className="relative flex items-center justify-center">
          <span className="absolute h-5 w-5 rounded-full bg-purple-500/30 blur-[3px]" />
          <MapPin className="relative h-7 w-7 fill-purple-600 text-white drop-shadow" />
        </span>
      </div>
    </div>
  );
};
